import Pusher from "pusher-js";
import { isProduction } from "Data/Objects/System";
import { ActionCreatorWithPayload, AsyncThunk, AsyncThunkAction } from "@reduxjs/toolkit";
import { AsyncThunkConfig, store } from "Data/Redux/Store";
import { AppState } from "Data/Redux/Reducers";
import { IPusherEvent } from "Interfaces";
import { createUserPusherAction, updateUserPusherAction } from "Data/Redux/Slices/User";
import { createOrderAndFetchItems, deleteOrderLocal, updateOrderAndFilterItems } from "Data/Actions/Pusher";
import { IWithApiData } from "@clintonelec/typescriptutils";

type EventAction<TPayload, TReturn = void> = AsyncThunk<TPayload, TReturn, AsyncThunkConfig>
	| ActionCreatorWithPayload<TPayload>
	| ((data: IWithApiData<unknown>) => AsyncThunkAction<unknown, unknown, AsyncThunkConfig>);

type EventBinding<TPayload, TReturn = void> = {
	action?: EventAction<TPayload, TReturn>;
	callback?: () => void;
	channel?: Record<string, EventAction<TPayload, TReturn>>;
};

const deleteWrapper = (actionCreator: AsyncThunk<void, string, AsyncThunkConfig>) => {
	return (notification: IWithApiData<unknown>) => {
		return actionCreator(notification.data?.id ?? "");
	};
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const EVENT_BINDINGS: Record<string, Record<string, EventBinding<any, any>>> = {
	order: {
		created: {
			action: createOrderAndFetchItems
		},
		updated: {
			action: updateOrderAndFilterItems
		},
		deleted: {
			action: deleteWrapper(deleteOrderLocal)
		}
	},
	user: {
		created: {
			action: createUserPusherAction
		},
		updated: {
			action: updateUserPusherAction
		}
	}
};

export class PusherConnection {
	pusherConnection?: Pusher.Pusher;
	globalChannel?: Pusher.Channel;

	initializeConnection = () => {
		this.connectPusher();
		this.connectGlobalChannel();
	};

	connectPusher = () => {
		const state: AppState = store.getState();
		const { users: { user, token } } = state;
		const pusherkey = isProduction ? "Zg26Zj9l5TACPDqdnk3W" : "ePCIVAHhz5wHtqNtHjdx";
		const authEndpoint = isProduction
			? "https://nephele.clintonelectronics.com/api/broadcasting/auth"
			: "https://develop.nephele.clintonelectronics.com/api/broadcasting/auth";

		if (user && !this.pusherConnection) {
			this.pusherConnection = new Pusher(pusherkey, {
				authEndpoint: authEndpoint,
				auth: {
					headers: {
						"Authorization": "Bearer " + token
					}
				},
				disableStats: true,
				enabledTransports: [ "ws", "wss" ],
				encrypted: true,
				forceTLS: true,
				wsHost: "conloquium.clintonconnect.com"
			});
		}
	};

	rebindEvents = () => {
		this.connectPusher();
		this.connectGlobalChannel(true);
	};

	connectGlobalChannel = (reset?: boolean) => {
		const channelName = "global";

		if (!this.globalChannel || this.globalChannel.name !== channelName) {
			this.pusherConnection?.allChannels().forEach((channel: Pusher.Channel) => {
				if (channel.name.indexOf("global") >= 0) {
					this.pusherConnection?.unsubscribe(channel.name);
				}
			});

			this.globalChannel = this.pusherConnection?.subscribe(channelName);
		}

		if (!this.globalChannel?.subscribed || reset) {
			this.globalChannel?.unbind_global();
			this.globalChannel?.bind_global(this.createPusherHandler(channelName));
		}
	};

	createPusherHandler = (channel: string) => {
		return (eventType: string, notification: IPusherEvent) => {
			if (!notification || !channel) {
				return;
			}

			const channelNotification = { Channel: channel, Notification: { ...notification } };

			console.log(channelNotification);

			const actionCreator =
				EVENT_BINDINGS?.[notification.type]?.[notification.event]?.channel?.[channel]
				?? EVENT_BINDINGS?.[notification.type]?.[notification.event]?.action;

			const callback = EVENT_BINDINGS?.[notification.type]?.[notification.event]?.callback;

			if (actionCreator) {
				notification.entities.forEach((id: string) => {
					if (notification.batch) {
						notification.data.forEach(data => {
							Promise.resolve(store.dispatch(actionCreator(
								{ data: { ...data } } as unknown as IWithApiData<unknown, unknown>
							))).then(callback);
						});
					} else {
						Promise.resolve(store.dispatch(actionCreator(
							{ data: { ...notification.data, id } } as unknown as IWithApiData<unknown, unknown>
						))).then(callback);
					}
				});
			}
		};
	};

	disconnect() {
		this.pusherConnection?.allChannels().map((channel: Pusher.Channel) => {
			this.pusherConnection?.unsubscribe(channel.name);
		});

		this.pusherConnection?.unbind_all();
		this.pusherConnection?.disconnect();

		delete this.pusherConnection;
		delete this.globalChannel;
	}
}

export const pusherConnection = new PusherConnection();
